import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  AlertTriangle, 
  Clock, 
  Mail, 
  Phone,
  CheckCircle,
  Download,
  Bell
} from "lucide-react";

const formatCurrency = (amount, devise = "FCFA") => {
  if (devise === "EUR") {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);
  }
  return new Intl.NumberFormat("fr-FR").format(amount) + " FCFA";
};

const urgenceConfig = {
  critique: { label: "Critique", className: "bg-red-900/50 text-red-300 border-red-800", icon: AlertTriangle },
  haute: { label: "Haute", className: "bg-amber-900/50 text-amber-300 border-amber-800", icon: Clock },
  normale: { label: "Normale", className: "bg-blue-900/50 text-blue-300 border-blue-800", icon: Bell },
};

export default function Relances() {
  const [relances, setRelances] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [relancesRes, statsRes] = await Promise.all([
        apiClient.get("/relances"),
        apiClient.get("/relances/stats"),
      ]);
      setRelances(relancesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des relances");
    } finally {
      setLoading(false);
    }
  };

  const handleMarquerRelance = async (paiementId) => {
    try {
      await apiClient.post(`/relances/${paiementId}/marquer-relance`);
      toast.success("Relance enregistrée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleMarquerPaye = async (paiementId) => {
    try {
      await apiClient.put(`/paiements/${paiementId}`, {
        statut: "paye",
        date_paiement: new Date().toISOString().split("T")[0],
      });
      toast.success("Paiement marqué comme payé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="relances-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Relances</h1>
        <p className="text-zinc-500 mt-1">Paiements en retard à relancer</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#121214] border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500">Total en retard</p>
                  <p className="text-3xl font-heading font-bold text-red-400">{stats.total_retard}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#121214] border-zinc-800">
            <CardContent className="p-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Montant FCFA</p>
                <p className="text-xl font-mono font-bold text-white">{formatCurrency(stats.montant_retard_fcfa)}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#121214] border-zinc-800">
            <CardContent className="p-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Montant EUR</p>
                <p className="text-xl font-mono font-bold text-white">{formatCurrency(stats.montant_retard_eur, "EUR")}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#121214] border-zinc-800">
            <CardContent className="p-4">
              <div className="flex gap-4">
                <div className="text-center">
                  <p className="text-xs text-red-400">Critique</p>
                  <p className="text-xl font-bold text-red-400">{stats.par_urgence?.critique || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-amber-400">Haute</p>
                  <p className="text-xl font-bold text-amber-400">{stats.par_urgence?.haute || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-blue-400">Normale</p>
                  <p className="text-xl font-bold text-blue-400">{stats.par_urgence?.normale || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Relances List */}
      <div className="space-y-4">
        {relances.length === 0 ? (
          <Card className="bg-[#121214] border-zinc-800">
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
              <p className="text-zinc-300 font-medium">Aucun paiement en retard</p>
              <p className="text-zinc-500 text-sm mt-1">Tous les paiements sont à jour</p>
            </CardContent>
          </Card>
        ) : (
          relances.map((relance) => {
            const UrgenceIcon = urgenceConfig[relance.urgence]?.icon || Clock;
            
            return (
              <Card 
                key={relance.paiement.id} 
                className={`bg-[#121214] border-zinc-800 ${
                  relance.urgence === 'critique' ? 'border-l-4 border-l-red-500' :
                  relance.urgence === 'haute' ? 'border-l-4 border-l-amber-500' : ''
                }`}
                data-testid={`relance-card-${relance.paiement.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        relance.urgence === 'critique' ? 'bg-red-900/30' :
                        relance.urgence === 'haute' ? 'bg-amber-900/30' : 'bg-blue-900/30'
                      }`}>
                        <UrgenceIcon className={`w-5 h-5 ${
                          relance.urgence === 'critique' ? 'text-red-400' :
                          relance.urgence === 'haute' ? 'text-amber-400' : 'text-blue-400'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-white">{relance.mission?.titre}</h3>
                          <Badge variant="outline" className={urgenceConfig[relance.urgence]?.className}>
                            {relance.jours_retard} jours de retard
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                          <div>
                            <p className="text-sm text-zinc-400">Client</p>
                            <p className="text-white">{relance.client?.nom} - {relance.client?.entreprise}</p>
                          </div>
                          <div>
                            <p className="text-sm text-zinc-400">Contact</p>
                            <div className="flex items-center gap-4">
                              {relance.client?.email && (
                                <a href={`mailto:${relance.client.email}`} className="flex items-center gap-1 text-blue-400 hover:underline text-sm">
                                  <Mail className="w-3 h-3" />
                                  {relance.client.email}
                                </a>
                              )}
                              {relance.client?.telephone && (
                                <span className="flex items-center gap-1 text-zinc-300 text-sm">
                                  <Phone className="w-3 h-3" />
                                  {relance.client.telephone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-sm text-zinc-500 mt-2">
                          {relance.paiement.notes} • Échéance: {relance.paiement.date_prevue}
                          {relance.paiement.nombre_relances > 0 && (
                            <span className="ml-2 text-amber-400">
                              ({relance.paiement.nombre_relances} relance{relance.paiement.nombre_relances > 1 ? 's' : ''})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xl font-mono font-bold text-white">
                          {formatCurrency(relance.montant, relance.devise)}
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarquerRelance(relance.paiement.id)}
                          className="border-amber-700 text-amber-400 hover:bg-amber-900/30"
                          data-testid={`relance-btn-${relance.paiement.id}`}
                        >
                          <Bell className="w-4 h-4 mr-1" />
                          Relancer
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleMarquerPaye(relance.paiement.id)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`paye-btn-${relance.paiement.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Payé
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
